import React, { useCallback, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { useClickOutside } from '../../hooks/useClickOutside';
export interface DropdownOption {
  value: string;
  label: string;
  group?: string;
}
interface FilterDropdownProps {
  label: string;
  options: DropdownOption[];
  value: string[];
  onChange: (v: string[]) => void;
  multiple?: boolean;
  searchable?: boolean;
  allLabel?: string;
  disabled?: boolean;
  disabledReason?: string;
  menuWidth?: number;
  showAllOption?: boolean;
}
export function FilterDropdown({
  label,
  options,
  value,
  onChange,
  multiple = false,
  searchable = false,
  allLabel = 'All',
  disabled = false,
  disabledReason,
  menuWidth = 240,
  showAllOption = true
}: FilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);
  useClickOutside(ref, close, open);
  const display = value.length === 0 ? allLabel : value.length === 1 ? options.find(o => o.value === value[0])?.label ?? value[0] : `${value.length} selected`;
  const filtered = query ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase())) : options;
  const select = (v: string) => {
    if (!multiple) {
      onChange([v]);
      close();
      return;
    }
    onChange(value.includes(v) ? value.filter(x => x !== v) : [...value, v]);
  };
  let lastGroup: string | undefined;
  return <div ref={ref} className="relative" title={disabled ? disabledReason : undefined} data-id="element-663" data-mp="387xw79ui7ppo">
      <button type="button" disabled={disabled} aria-label={disabled && disabledReason ? `${label}: ${disabledReason}` : undefined} onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open} className={twMerge('flex h-8 items-center gap-1.5 rounded-md border bg-white pl-2.5 pr-1.5 text-left text-[12.5px] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy/30', value.length ? 'border-navy/40 bg-navy-50' : 'border-line hover:border-line-strong', disabled && 'cursor-not-allowed border-dashed bg-canvas opacity-60 hover:border-line')} data-id="element-664" data-mp="3d6rd7c901yul">
        <span className="whitespace-nowrap text-ink-muted" data-id="element-665" data-mp="2y5u8tnkes1d6">{label}</span>
        <span className="max-w-[150px] truncate font-semibold text-ink" data-id="element-666" data-mp="2y6lrjxswq4m1">{disabled ? 'Not applied' : display}</span>
        <ChevronDownIcon className={`h-3.5 w-3.5 text-ink-subtle transition-transform duration-150 ${open ? 'rotate-180' : ''}`} data-id="element-667" data-mp="2t8katjiyehdq" />
      </button>
      <AnimatePresence data-id="element-668" data-mp="1hru9adph6dqg">
        {open && <motion.div initial={{
        opacity: 0,
        scale: 0.96,
        y: -4
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: -4
      }} transition={{
        duration: 0.15,
        ease: [0.23, 1, 0.32, 1]
      }} style={{
        width: menuWidth,
        transformOrigin: 'top left'
      }} className="absolute left-0 z-40 mt-1.5 overflow-hidden rounded-lg border border-line bg-white shadow-lg" data-id="element-669" data-mp="1hphirc5niv1s">
            {searchable && <div className="border-b border-line p-2" data-id="element-670" data-mp="70nvqu7rmw6x">
                <input autoFocus type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder={`Search ${label.toLowerCase()}…`} className="h-8 w-full rounded border border-line px-2.5 text-xs focus:border-navy/50 focus:outline-none" data-id="element-671" data-mp="bz7iuxy3ie0v" />
              </div>}
            <ul role="listbox" aria-multiselectable={multiple} className="max-h-72 overflow-auto py-1" data-id="element-672" data-mp="2mrire46larez">
              {showAllOption && !query && <li data-id="element-673" data-mp="2rreve3faeiuj">
                  <button type="button" onClick={() => {
              onChange([]);
              if (!multiple) close();
            }} className="flex w-full items-center justify-between px-3 py-1.5 text-left text-[12.5px] font-medium text-ink hover:bg-canvas" data-id="element-674" data-mp="m0s20yer1wbh">
                    {allLabel}
                    {value.length === 0 && <CheckIcon className="h-3.5 w-3.5 text-navy" data-id="element-675" data-mp="3bv5envvqrq3" />}
                  </button>
                </li>}
              {filtered.map(o => {
            const selected = value.includes(o.value);
            const header = o.group && o.group !== lastGroup ? o.group : null;
            lastGroup = o.group;
            return <React.Fragment key={o.value} data-id="element-676" data-mp="3frytukxkd3on">
                    {header && <li className="px-3 pb-1 pt-2.5 text-[11px] font-semibold text-ink-subtle" data-id="element-677" data-mp="3fqdv0j47uykz">{header}</li>}
                    <li role="option" aria-selected={selected} data-id="element-678" data-mp="3boiv9j1xot9b">
                      <button type="button" onClick={() => select(o.value)} className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12.5px] text-ink hover:bg-canvas" data-id="element-679" data-mp="31lwuhnd1oa1o">
                        {multiple && <span className={`grid h-3.5 w-3.5 shrink-0 place-items-center rounded-[3px] border ${selected ? 'border-navy bg-navy text-white' : 'border-line-strong'}`} data-id="element-680" data-mp="2wnv9gww260nk">
                            {selected && <CheckIcon className="h-2.5 w-2.5" strokeWidth={3} data-id="element-681" data-mp="3afi996p7lrb3" />}
                          </span>}
                        <span className="flex-1 truncate" data-id="element-682" data-mp="3fciexgi5cs1o">{o.label}</span>
                        {!multiple && selected && <CheckIcon className="h-3.5 w-3.5 text-navy" data-id="element-683" data-mp="1yya6sqy4il9k" />}
                      </button>
                    </li>
                  </React.Fragment>;
          })}
              {filtered.length === 0 && <li className="px-3 py-4 text-center text-xs text-ink-muted" data-id="element-684" data-mp="aiachqqpvkh9">No matches</li>}
            </ul>
            {multiple && value.length > 0 && <div className="flex items-center justify-between border-t border-line px-3 py-2 text-xs" data-id="element-685" data-mp="pcnd1r7yx9o">
                <span className="text-ink-muted" data-id="element-686" data-mp="3rvw5z33arrij">{value.length} selected</span>
                <button type="button" onClick={() => onChange([])} className="font-medium text-navy hover:underline" data-id="element-687" data-mp="3mxunokdp3w84">
                  Clear
                </button>
              </div>}
          </motion.div>}
      </AnimatePresence>
    </div>;
}